import type { Edge } from '@xyflow/react';

/**
 * Graph traversal and connection compatibility utilities for workflow editors.
 *
 * Key concepts:
 * - "Downstream" = nodeB is downstream of nodeA if there's a path A -> ... -> B
 * - "Upstream" = nodeB is upstream of nodeA if there's a path B -> ... -> A
 * - Flow connections must go forward (upstream -> downstream) to prevent cycles
 */

/**
 * Check if there's a directed path from `fromNode` to `toNode` following edges.
 * Uses BFS for efficient traversal.
 *
 * @param fromNode - Starting node ID
 * @param toNode - Target node ID to reach
 * @param edges - All edges in the graph
 * @returns true if a path exists from fromNode to toNode
 */
export function hasPath(
  fromNode: string,
  toNode: string,
  edges: Edge[],
): boolean {
  if (fromNode === toNode) return true;

  const visited = new Set<string>();
  const queue = [fromNode];

  while (queue.length > 0) {
    const current = queue.shift();
    if (current === undefined) continue;
    if (visited.has(current)) continue;
    visited.add(current);

    // Find all nodes this node connects TO (outgoing edges)
    for (const edge of edges) {
      if (edge.source === current) {
        if (edge.target === toNode) return true;
        queue.push(edge.target);
      }
    }
  }

  return false;
}

/**
 * Check if nodeB is downstream of nodeA (i.e., there's a path from A to B).
 * If B is downstream of A, then A executes before B.
 */
export function isDownstream(
  nodeA: string,
  nodeB: string,
  edges: Edge[],
): boolean {
  return hasPath(nodeA, nodeB, edges);
}

/**
 * Check if nodeB is upstream of nodeA (i.e., there's a path from B to A).
 * If B is upstream of A, then B executes before A.
 */
export function isUpstream(
  nodeA: string,
  nodeB: string,
  edges: Edge[],
): boolean {
  return hasPath(nodeB, nodeA, edges);
}

/**
 * Context for checking flow control connection validity.
 */
export interface FlowConnectionContext {
  /** The node being evaluated for compatibility */
  thisNodeId: string;
  /** The type of handle on this node ('source' for output, 'target' for input) */
  thisHandleType: 'source' | 'target';
  /** The node where the drag originated */
  dragOriginNodeId: string;
  /** The type of handle where drag started */
  dragOriginHandleType: 'source' | 'target';
  /** All edges in the graph */
  edges: Edge[];
}

/**
 * Check if a flow control connection would be valid.
 *
 * Rules:
 * 1. Can't connect to the same node
 * 2. Source handles connect to target handles (and vice versa)
 * 3. Connections must not create cycles:
 *    - When dragging FROM an output (source), the new edge is: dragOrigin -> thisNode
 *      Invalid if thisNode can already reach dragOrigin (would create cycle)
 *    - When dragging FROM an input (target), the new edge is: thisNode -> dragOrigin
 *      Invalid if dragOrigin can already reach thisNode (would create cycle)
 *
 * @returns true if the connection would be valid
 */
export function isFlowConnectionValid(ctx: FlowConnectionContext): boolean {
  const {
    thisNodeId,
    thisHandleType,
    dragOriginNodeId,
    dragOriginHandleType,
    edges,
  } = ctx;

  // Rule 1: Can't connect to the same node
  if (thisNodeId === dragOriginNodeId) return false;

  // Rule 2: Must be opposite handle types (source -> target or target -> source)
  if (thisHandleType === dragOriginHandleType) return false;

  // Rule 3: Check that the new edge wouldn't create a cycle
  if (dragOriginHandleType === 'source') {
    // Dragging FROM an output (source), connecting TO an input (target)
    // New edge would be: dragOrigin -> thisNode
    // Cycle if: thisNode can already reach dragOrigin (path exists: thisNode -> ... -> dragOrigin)
    return !hasPath(thisNodeId, dragOriginNodeId, edges);
  } else {
    // Dragging FROM an input (target), connecting TO an output (source)
    // New edge would be: thisNode -> dragOrigin
    // Cycle if: dragOrigin can already reach thisNode (path exists: dragOrigin -> ... -> thisNode)
    return !hasPath(dragOriginNodeId, thisNodeId, edges);
  }
}

/**
 * Check if connecting `sourceNodeId` -> `targetNodeId` would be valid
 * (i.e., wouldn't create a cycle).
 *
 * @param sourceNodeId - The node that would be the source of the new edge
 * @param targetNodeId - The node that would be the target of the new edge
 * @param edges - All existing edges in the graph
 * @returns true if the connection would be valid (no cycle)
 */
export function isValidConnection(
  sourceNodeId: string,
  targetNodeId: string,
  edges: Edge[],
): boolean {
  // Can't connect to self
  if (sourceNodeId === targetNodeId) return false;

  // Check if target is already upstream of source (would create cycle)
  // If there's a path from target -> source, adding source -> target creates a cycle
  return !hasPath(targetNodeId, sourceNodeId, edges);
}
